import React, { useCallback, useEffect, useState } from 'react'
import { Button, Heading, LoadingSpinner, Text } from '../../components/ui'
import { PermissionGuard } from '../../components/PermissionGuard'
import { marketingParametersService } from '../../services/marketingParameters'
import {
  MARKETING_PARAM_DEPARTAMENTO,
  type MarketingParameter,
} from '../../types/marketingParameters'
import { PARAMS_MODULE_NAME, PERM_LOADING, SELECT_CLASS } from './solicitacaoUi'

const MarketingParametrosTab: React.FC = () => {
  const [loading, setLoading] = useState(true)
  const [parameters, setParameters] = useState<MarketingParameter[]>([])
  const [departments, setDepartments] = useState<
    { id: string; name: string; display_name: string }[]
  >([])
  const [editingParameter, setEditingParameter] = useState<MarketingParameter | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editValue, setEditValue] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [formError, setFormError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [params, deps] = await Promise.all([
        marketingParametersService.listParameters(),
        marketingParametersService.listDepartmentsActive(),
      ])
      setParameters(params)
      setDepartments(deps)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar parâmetros.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const isDepartamentoMarketingParam = editingParameter?.key === MARKETING_PARAM_DEPARTAMENTO

  const openEdit = (param: MarketingParameter) => {
    setEditingParameter(param)
    setEditValue(param.value)
    setFormError(null)
    setIsModalOpen(true)
  }

  const closeModal = () => {
    setIsModalOpen(false)
    setEditingParameter(null)
    setEditValue('')
    setFormError(null)
  }

  const save = async () => {
    if (!editingParameter || saving) return
    setSaving(true)
    setFormError(null)
    try {
      await marketingParametersService.updateParameter(editingParameter.id, {
        value: editValue.trim(),
      })
      closeModal()
      await load()
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Erro ao salvar parâmetro.')
    } finally {
      setSaving(false)
    }
  }

  const displayValue = (param: MarketingParameter) => {
    if (param.key === MARKETING_PARAM_DEPARTAMENTO) {
      if (!param.value.trim()) return '—'
      const dept = departments.find((d) => d.id === param.value)
      return dept?.display_name || param.value
    }
    const raw = String(param.value)
    return raw.length > 40 ? `${raw.slice(0, 40)}…` : raw || '—'
  }

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <LoadingSpinner />
      </div>
    )
  }

  return (
    <>
      <div className="mb-4">
        <Text size="sm" color="muted">
          Parâmetros de configuração do módulo de marketing. Edite o valor conforme necessário.
        </Text>
      </div>

      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">
          {error}
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-900/30">
            <tr>
              <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                Chave
              </th>
              <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                Descrição
              </th>
              <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                Valor
              </th>
              <th className="px-4 py-2" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {parameters.length === 0 ? (
              <tr>
                <td
                  colSpan={4}
                  className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400"
                >
                  Nenhum parâmetro cadastrado.
                </td>
              </tr>
            ) : (
              parameters.map((param) => (
                <tr key={param.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                  <td className="px-4 py-2 font-mono text-sm text-gray-700 dark:text-gray-300">
                    {param.key}
                  </td>
                  <td
                    className="max-w-xs truncate px-4 py-2 text-sm text-gray-600 dark:text-gray-300"
                    title={param.description ?? undefined}
                  >
                    {param.description ?? '—'}
                  </td>
                  <td className="px-4 py-2 text-sm text-gray-900 dark:text-white">
                    {displayValue(param)}
                  </td>
                  <td className="px-4 py-2 text-right">
                    <PermissionGuard
                      moduleName={PARAMS_MODULE_NAME}
                      permissionType="update"
                      fallback={null}
                      loadingComponent={PERM_LOADING}
                    >
                      <Button variant="outline" size="sm" onClick={() => openEdit(param)}>
                        Editar
                      </Button>
                    </PermissionGuard>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {isModalOpen && editingParameter && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div
            className="w-full max-w-2xl rounded-lg bg-white p-6 shadow-xl dark:bg-gray-800"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-4 flex items-center justify-between">
              <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">
                Editar parâmetro
              </Heading>
              <button
                type="button"
                onClick={closeModal}
                className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <Text size="sm" className="mb-4 block font-mono text-gray-500 dark:text-gray-400">
              {editingParameter.key}
            </Text>

            {formError && (
              <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">
                {formError}
              </div>
            )}

            <div className="space-y-4">
              <div>
                <Text size="sm" className="mb-2 text-gray-700 dark:text-gray-300">
                  Valor
                </Text>
                {isDepartamentoMarketingParam ? (
                  <select
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    className={SELECT_CLASS + ' w-full'}
                    disabled={saving}
                  >
                    <option value="">- Nenhum -</option>
                    {departments.map((d) => (
                      <option key={d.id} value={d.id}>
                        {d.display_name}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="text"
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    className={SELECT_CLASS + ' w-full'}
                    disabled={saving}
                  />
                )}
              </div>

              <div>
                <Text size="sm" className="mb-1 block text-gray-700 dark:text-gray-300">
                  Descrição
                </Text>
                <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-600 dark:border-gray-600 dark:bg-gray-900/50 dark:text-gray-400">
                  {editingParameter.description ?? '—'}
                </div>
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <Button variant="outline" onClick={closeModal} disabled={saving}>
                Cancelar
              </Button>
              <PermissionGuard
                moduleName={PARAMS_MODULE_NAME}
                permissionType="update"
                fallback={null}
                loadingComponent={PERM_LOADING}
              >
                <Button variant="solid" onClick={() => void save()} disabled={saving}>
                  {saving ? 'Salvando...' : 'Salvar'}
                </Button>
              </PermissionGuard>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default MarketingParametrosTab
